import React, { useState, useCallback } from "react";
import { format, parseISO, subDays, startOfDay, endOfDay } from "date-fns";
import { useHotel } from "../../contexts/HotelContext";
import Layout from "../../components/Layout";
import { useEmailTrackingDashboard } from "../../hooks/useEmailTrackingDashboard";
import { DateRangePicker } from "../../components/admin/llm-usage";
import {
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  AccessDeniedState,
} from "../../components/admin/llm-usage";
import {
  EmailTrackingSummaryCards,
  ClicksByCategoryChart,
  StatsByDayChart,
  TopClickedCalls,
  TopUsers,
  InactiveTenants,
} from "../../components/admin/email-tracking";

/**
 * Format date as YYYY-MM-DD string (no timezone conversion)
 */
const formatDateOnly = (date: Date): string => {
  return format(date, "yyyy-MM-dd");
};

const getDefaultDateRange = () => {
  const now = new Date();
  const start = startOfDay(subDays(now, 30));
  const end = endOfDay(now);
  return {
    from: formatDateOnly(start),
    to: formatDateOnly(end),
  };
};

const EmailTracking: React.FC = () => {
  const { user, hasModuleEnabled } = useHotel();

  // Date range state
  const [dateRange, setDateRange] = useState(getDefaultDateRange);

  // Check access — mirror ModeratorRoute: global admin OR kiedynabory
  // moderator module. Tenant-level grants:schedule is enforced by the
  // backend @RequiredModule('grants:schedule') ModuleGuard.
  const canModerate =
    user?.role === "admin" || hasModuleEnabled("admin:kiedynabory:write");

  // Query hook — gated by access check to avoid unauthorized API calls
  const {
    data,
    isLoading,
    isRefetching,
    error,
    refetch,
  } = useEmailTrackingDashboard({
    from: dateRange.from,
    to: dateRange.to,
    enabled: canModerate,
  });

  // Handle date change - DateRangePicker sends startDate/endDate, we convert to from/to
  const handleDateChange = useCallback((startDate: string, endDate: string) => {
    setDateRange({ from: startDate, to: endDate });
  }, []);

  // Redirect users without moderator access
  if (!canModerate) {
    return (
      <Layout>
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 text-left">
              Statystyki maili
            </h1>
          </div>
          <AccessDeniedState message="Ta strona jest dostępna tylko dla administratorów z dostępem do modułu grants:schedule." />
        </div>
      </Layout>
    );
  }

  // Determine if data is empty (nothing was sent AND no engagement events)
  const isEmpty =
    data &&
    data.totalSent === 0 &&
    data.totalOpens === 0 &&
    data.totalClicks === 0;

  return (
    <Layout>
      <div className="relative space-y-6" data-testid="email-tracking-page" aria-busy={isRefetching}>
        {/* Refetch Overlay */}
        {isRefetching && !isLoading && (
          <div
            className="absolute inset-0 bg-white/50 flex items-center justify-center z-10"
            aria-live="polite"
          >
            <span className="sr-only">Aktualizowanie danych</span>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">
              Statystyki maili
            </h1>
            <p className="text-neutral-600 mt-1">
              Monitoruj zaangażowanie użytkowników w maile alertowe z naborów
            </p>
          </div>
        </div>

        {/* Date Range Picker */}
        <div className="bg-white rounded-lg border border-neutral-200 p-4">
          <DateRangePicker
            startDate={dateRange.from}
            endDate={dateRange.to}
            onDateChange={handleDateChange}
            maxDays={365}
          />
          {data && (
            <div className="mt-2 text-sm text-neutral-500">
              Dane od{" "}
              <span className="font-medium">
                {format(parseISO(data.dateRange.startDate), "d MMM yyyy")}
              </span>{" "}
              do{" "}
              <span className="font-medium">
                {format(parseISO(data.dateRange.endDate), "d MMM yyyy")}
              </span>
            </div>
          )}
        </div>

        {/* Loading State */}
        {isLoading && <LoadingSkeleton />}

        {/* Error State */}
        {error && <ErrorState error={error} onRetry={() => refetch()} />}

        {/* Empty State */}
        {!isLoading && !error && isEmpty && <EmptyState />}

        {/* Dashboard Content */}
        {!isLoading && !error && data && !isEmpty && (
          <>
            {/* Summary Cards */}
            <EmailTrackingSummaryCards
              totalSent={data.totalSent}
              totalOpens={data.totalOpens}
              totalClicks={data.totalClicks}
              openRate={data.openRate}
              clickToOpenRate={data.clickToOpenRate}
            />

            {/* Per-day funnel (sent / opens / clicks) — full width */}
            <StatsByDayChart data={data.statsByDay} />

            {/* Clicks by category */}
            <ClicksByCategoryChart data={data.clicksByCategory} />

            {/* Top Clicked Calls */}
            <TopClickedCalls data={data.topClickedCalls} />

            {/* Engagement leaderboards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <TopUsers data={data.topUsers} />
              <InactiveTenants data={data.inactiveTenants} />
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default EmailTracking;
